import * as React from "react"
import {
  loadStripe,
  type Stripe,
  type Appearance,
  type StripeElementsOptions,
} from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"

// SSR-safe Stripe Elements wrapper, themed from the design-system tokens and
// reactive to light/dark switches (watches the .dark class on <html>).
//
// Two modes:
//   clientSecret — classic flow, confirms against an existing intent
//                  (lifetime deals).
//   deferred     — the Payment Element renders before any intent exists
//                  (subscription checkout); the caller fetches the client
//                  secret at confirm time and passes it to
//                  stripe.confirmPayment. Amount updates propagate via
//                  elements.update() when the prop changes.

let stripePromise: Promise<Stripe | null> | null = null

function getStripe(publishableKey: string): Promise<Stripe | null> {
  if (!stripePromise) stripePromise = loadStripe(publishableKey)
  return stripePromise
}

function cssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

function buildAppearance(): Appearance {
  const accent = cssVar("--color-accent", "#1f6feb")
  const hairline = cssVar("--color-hairline", "#e2e8f0")
  return {
    theme: "stripe",
    variables: {
      colorPrimary: accent,
      colorBackground: cssVar("--color-page", "#ffffff"),
      colorText: cssVar("--color-ink-body", "#1f1f1c"),
      colorTextSecondary: cssVar("--color-ink-muted", "#6b6b64"),
      colorDanger: cssVar("--color-danger-display", "#b42318"),
      fontFamily: "'DM Sans', system-ui, sans-serif",
      borderRadius: "8px",
    },
    rules: {
      ".Input": { borderColor: hairline },
      ".Tab": { borderColor: hairline },
      ".Tab:hover": { borderColor: accent, color: accent },
      ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
        borderColor: accent,
        boxShadow: `0 0 0 1px ${accent}`,
        color: accent,
      },
    },
  }
}

export interface DeferredElementsConfig {
  mode: "payment" | "setup" | "subscription"
  amount: number
  currency: string
  paymentMethodTypes: string[]
}

const STRIPE_FONTS = [
  { cssSrc: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap" },
]

export function StripeProvider({
  publishableKey,
  clientSecret,
  deferred,
  children,
}: {
  publishableKey: string
  clientSecret?: string
  deferred?: DeferredElementsConfig
  children: React.ReactNode
}) {
  const [mounted, setMounted] = React.useState(false)
  const [appearance, setAppearance] = React.useState<Appearance | null>(null)

  React.useEffect(() => {
    setMounted(true)
    setAppearance(buildAppearance())

    const observer = new MutationObserver(() => setAppearance(buildAppearance()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])

  if (!mounted || !appearance) return null

  const options = (
    clientSecret
      ? { clientSecret, appearance, fonts: STRIPE_FONTS }
      : {
          mode: deferred!.mode,
          amount: Math.max(deferred!.amount, 1),
          currency: deferred!.currency,
          paymentMethodTypes: deferred!.paymentMethodTypes,
          appearance,
          fonts: STRIPE_FONTS,
        }
  ) as StripeElementsOptions

  return (
    <Elements stripe={getStripe(publishableKey)} options={options}>
      {children}
    </Elements>
  )
}
